import { useEffect, useMemo, useRef, useState } from 'react'
import { useController, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useMediaQuery } from '@mui/material'

import ArrowRight from '../../assets/icons/ArrowRight'
import ListIcon from '../../assets/icons/ListIcon'
import SelectAngleIcon from '../../assets/icons/SelectAngleIcon'
import CustomButton from '../../components/Button'
import CheckboxGroup from '../../components/CheckboxGroup'
import Form from '../../components/Form'
import Select from '../../components/Select'
import Summary from '../../components/Summary'
import Toast from '../../components/Toast'
import { selectAddons } from '../../store/enrolment/enrolment.actions'
import { selectOrder, selectProductOrders } from '../../store/enrolment/enrolment.selectors'
import { Wrapper } from './styled'

import { useDispatch, useSelector } from 'react-redux'

const ALLOWED_HTML_TAGS = new Set(['a', 'br', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u', 'span'])

const sanitizeHtml = html => {
  if (!html) return ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const sanitizeNode = node => {
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const element = node
    const tagName = element.tagName.toLowerCase()

    if (!ALLOWED_HTML_TAGS.has(tagName)) {
      const parent = element.parentNode

      if (parent) {
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element)
        }

        parent.removeChild(element)
      }

      return
    }

    const allowedAttributes = tagName === 'a' ? ['href', 'target', 'rel'] : []

    Array.from(element.attributes).forEach(attribute => {
      if (!allowedAttributes.includes(attribute.name.toLowerCase())) {
        element.removeAttribute(attribute.name)
      }
    })

    if (tagName === 'a') {
      const href = element.getAttribute('href') || ''
      const isSafeHref =
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('/') ||
        href.startsWith('#')

      if (!isSafeHref) {
        element.removeAttribute('href')
      }

      if (href.startsWith('http://') || href.startsWith('https://')) {
        element.setAttribute('target', '_blank')
        element.setAttribute('rel', 'noopener noreferrer')
      }
    }

    Array.from(element.childNodes).forEach(sanitizeNode)
  }

  Array.from(doc.body.childNodes).forEach(sanitizeNode)

  return doc.body.innerHTML
}

const htmlToPlainText = html => {
  if (!html) return ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  doc.querySelectorAll('br').forEach(br => {
    br.replaceWith('\n')
  })

  doc.querySelectorAll('p, li').forEach(node => {
    node.insertAdjacentText('beforeend', '\n')
  })

  return (doc.body.textContent || '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim()
}

const splitTitleAndDescription = value => {
  const normalizedValue = (value || '').trim()

  if (!normalizedValue) {
    return { title: '', description: '' }
  }

  const [title = '', ...descriptionParts] = normalizedValue.split('\n')

  return {
    title: title.trim(),
    description: descriptionParts.join('\n').trim()
  }
}

const EnhanceYourExperienceScreen = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const dispatch = useDispatch()

  const productOrders = useSelector(selectProductOrders)
  const order = useSelector(selectOrder)

  const productOrder = productOrders[0] ?? {}
  const productOrderId = productOrder?.id ?? ''
  const hasDisplayedAddons = (productOrder?.displayedPackage?.addons?.length ?? 0) > 0

  const [isLoading, setIsLoading] = useState(false)
  const [addons, setAddons] = useState([])
  const [addonOptionErrors, setAddonOptionErrors] = useState({})
  const addonOptionRefs = useRef({})

  const isSmallScreen = useMediaQuery('(max-width: 1024px)')

  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      addons: [],
      addonOptions: {}
    }
  })

  const { field: addonsField } = useController({
    name: 'addons',
    control
  })

  const { field: addonOptionsField } = useController({
    name: 'addonOptions',
    control
  })

  const displayedAddons = useMemo(() => {
    return productOrder?.displayedPackage?.addons ?? []
  }, [productOrder?.displayedPackage?.addons])

  const displayedAddonsById = useMemo(() => {
    return displayedAddons.reduce((acc, addon) => {
      acc[addon.id] = addon

      return acc
    }, {})
  }, [displayedAddons])

  const selectedIds = useMemo(() => {
    return Array.isArray(addonsField.value) ? addonsField.value : []
  }, [addonsField.value])

  const selectedAddonOptions = useMemo(() => {
    return addonOptionsField.value && typeof addonOptionsField.value === 'object' ? addonOptionsField.value : {}
  }, [addonOptionsField.value])

  const getAddonOptionRefKey = (addonId, addonOptionId) => `${addonId}::${addonOptionId}`

  const clearAddonOptionError = (addonId, addonOptionId) => {
    setAddonOptionErrors(prev => {
      if (!prev?.[addonId]?.[addonOptionId]) return prev

      const nextAddonErrors = { ...(prev[addonId] ?? {}) }
      delete nextAddonErrors[addonOptionId]

      const nextErrors = { ...prev }

      if (Object.keys(nextAddonErrors).length) {
        nextErrors[addonId] = nextAddonErrors
      } else {
        delete nextErrors[addonId]
      }

      return nextErrors
    })
  }

  const clearAddonErrorsForUnselected = selectedAddonIds => {
    setAddonOptionErrors(prev => {
      const selectedSet = new Set(selectedAddonIds)
      const nextErrors = Object.entries(prev).reduce((acc, [addonId, addonErrors]) => {
        if (selectedSet.has(addonId)) {
          acc[addonId] = addonErrors
        }

        return acc
      }, {})

      if (Object.keys(nextErrors).length === Object.keys(prev).length) {
        return prev
      }

      return nextErrors
    })
  }

  const scrollToAddonOption = (addonId, addonOptionId) => {
    const refKey = getAddonOptionRefKey(addonId, addonOptionId)
    const fieldElement = addonOptionRefs.current[refKey]

    if (!fieldElement) return

    fieldElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })

    fieldElement.querySelector('input, [role="combobox"]')?.focus?.()
  }

  const validateRequiredAddonOptions = (addonIds, addonOptionsMap) => {
    const errors = {}
    let firstInvalidField = null

    addonIds.forEach(addonId => {
      const addon = displayedAddonsById[addonId]

      ;(addon?.addonOptions ?? []).forEach(option => {
        const selectedValue = addonOptionsMap?.[addonId]?.[option.id]

        if (!selectedValue) {
          if (!errors[addonId]) {
            errors[addonId] = {}
          }

          errors[addonId][option.id] = 'This field is required'

          if (!firstInvalidField) {
            firstInvalidField = { addonId, addonOptionId: option.id }
          }
        }
      })
    })

    return { errors, firstInvalidField }
  }

  const buildAddonsPayload = (addonIds, addonOptionsMap) => ({
    addons: addonIds.map(addonId => {
      const addon = displayedAddonsById[addonId]
      const addonOptions = addon?.addonOptions ?? []
      const selectedOptionsMap = addonOptionsMap?.[addonId] ?? {}

      return {
        addonId,
        options: addonOptions
          .map(option => ({
            addonOptionId: option.id,
            selectedValue: selectedOptionsMap[option.id]
          }))
          .filter(option => option.selectedValue)
      }
    })
  })

  const dispatchSelectedAddons = (addonIds, addonOptionsMap) => {
    dispatch(
      selectAddons({
        productOrderId,
        body: buildAddonsPayload(addonIds, addonOptionsMap)
      })
    )
  }

  const handleAddonsChange = (id, checked) => {
    const next = checked ? [...selectedIds, id] : selectedIds.filter(x => x !== id)

    addonsField.onChange(next)
    clearAddonErrorsForUnselected(next)
    dispatchSelectedAddons(next, selectedAddonOptions)
  }

  const handleAddonOptionChange = (addonId, addonOptionId, selectedValue) => {
    const nextAddonOptions = {
      ...selectedAddonOptions,
      [addonId]: {
        ...(selectedAddonOptions[addonId] ?? {}),
        [addonOptionId]: selectedValue
      }
    }

    addonOptionsField.onChange(nextAddonOptions)
    if (selectedValue) {
      clearAddonOptionError(addonId, addonOptionId)
    }
    dispatchSelectedAddons(selectedIds, nextAddonOptions)
  }

  const onSubmit = async data => {
    const addonIds = data.addons ?? []
    const addonOptionsMap = data.addonOptions ?? {}
    const { errors, firstInvalidField } = validateRequiredAddonOptions(addonIds, addonOptionsMap)

    if (firstInvalidField) {
      setAddonOptionErrors(errors)
      scrollToAddonOption(firstInvalidField.addonId, firstInvalidField.addonOptionId)

      return
    }

    setAddonOptionErrors({})
    setIsLoading(true)

    const paramsUrl = params.get('navigate')
    const navigateUrl = paramsUrl ? `/${paramsUrl}` : '/summary'

    try {
      await dispatch(
        selectAddons({
          productOrderId,
          body: buildAddonsPayload(addonIds, addonOptionsMap)
        })
      ).unwrap()

      navigate(navigateUrl)
    } catch (error) {
      // request errors are already surfaced by thunk toast
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!hasDisplayedAddons) {
      navigate('/summary', { replace: true })
    }
  }, [hasDisplayedAddons, navigate])

  useEffect(() => {
    const packageAddons = displayedAddons

    const payload = packageAddons
      .map(item => {
        const normalizedTitle = htmlToPlainText(item.title)
        const normalizedDescription = htmlToPlainText(item.description)
        const { title, description } = splitTitleAndDescription(normalizedTitle)

        return {
          id: item.id,
          name: [normalizedTitle, normalizedDescription].filter(Boolean).join('\n'),
          nameTitle: title,
          nameDescription: [description, normalizedDescription].filter(Boolean).join('\n'),
          isDiscount: false,
          tooltip: <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.tooltipText) }} />,
          price: item.price,
          addonOptions: item.addonOptions ?? []
        }
      })
      .sort((a, b) => {
        const aPrice = Number(a.price) || 0
        const bPrice = Number(b.price) || 0

        return aPrice - bPrice
      })

    setAddons(payload)
  }, [displayedAddons])

  useEffect(() => {
    const preselectedIds = productOrder?.selectedAddons?.map(item => item.id) ?? []
    const preselectedAddonOptions = (productOrder?.selectedAddons ?? []).reduce((acc, addon) => {
      acc[addon.id] = (addon.options ?? []).reduce((optionsAcc, option) => {
        if (option.selectedValue) {
          optionsAcc[option.id] = option.selectedValue
        }

        return optionsAcc
      }, {})

      return acc
    }, {})

    reset({
      addons: preselectedIds,
      addonOptions: preselectedAddonOptions
    })
  }, [productOrder?.selectedAddons, reset])

  if (!hasDisplayedAddons) {
    return null
  }

  return (
    <Wrapper>
      {isSmallScreen && (
        <Accordion
          className='accordion'
          disableGutters
          elevation={0}
          sx={{
            '&.Mui-expanded:first-of-type': { marginTop: '16px' }
          }}
        >
          <AccordionSummary expandIcon={<SelectAngleIcon />} className='accordion-summary'>
            <ListIcon />
            <Typography className='accordion-title'>Summary</Typography>
            <Typography className='accordion-title accordion-title-price'>
              £{new Intl.NumberFormat('en-IN').format(order.totalOrderAmount)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className='accordion-details'>
            <Summary />
          </AccordionDetails>
        </Accordion>
      )}

      <Typography className='title' component='h1'>
        Tailor Your <span className='title title-gold'>Experience</span>
      </Typography>

      <Typography className='text'>Select optional add-ons to customise your programme</Typography>

      <Form
        className='form'
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <>
            <CustomButton isLight onClick={() => navigate(-1)}>
              Back
            </CustomButton>

            <CustomButton type='submit' disabled={isLoading} className='form-footer-button'>
              Next <ArrowRight />
            </CustomButton>
          </>
        }
      >
        <CheckboxGroup
          groupName='addons'
          options={addons}
          value={selectedIds}
          onChange={handleAddonsChange}
          renderItemFooter={(addon, checked) => {
            if (!checked || !addon.addonOptions?.length) return null

            return (
              <Box className='addon-options'>
                {addon.addonOptions.map(option => (
                  <Box
                    key={`${addon.id}-${option.id}`}
                    className='addon-options-item'
                    ref={node => {
                      const refKey = getAddonOptionRefKey(addon.id, option.id)

                      if (node) {
                        addonOptionRefs.current[refKey] = node
                      } else {
                        delete addonOptionRefs.current[refKey]
                      }
                    }}
                    onClick={event => event.stopPropagation()}
                    onMouseDown={event => event.stopPropagation()}
                  >
                    <Typography className='addon-options-label'>{option.fieldDescription || option.title}</Typography>
                    <Select
                      className='addon-options-select'
                      placeholder='Select an option'
                      options={option.options ?? []}
                      value={selectedAddonOptions?.[addon.id]?.[option.id] ?? ''}
                      onChange={value => handleAddonOptionChange(addon.id, option.id, value)}
                      error={addonOptionErrors?.[addon.id]?.[option.id]}
                      disabled={!checked}
                      sx={{ '.MuiInputBase-root': { minHeight: '48px', height: '48px' } }}
                    />
                  </Box>
                ))}
              </Box>
            )
          }}
        />
      </Form>

      <Toast />
    </Wrapper>
  )
}

export default EnhanceYourExperienceScreen
